import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    getFilteredRowModel,
    createColumnHelper,
} from '@tanstack/react-table';

// Types
import { TableProps, dataTable, teamsColumnNames } from './types';

// Styled components
import {
    ContainerTable,
    TeamsTableContainer,
    TrStyled,
    FiltersContainer,
    TagsContainer,
    Tag,
    ActionButton,
    ActionCell,
} from './styles';

import {
    Table,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
} from '../../../summaryTables/SummaryTablesStyles';
import { Typography } from '../../../../Typography';

import SearchBar from '../../../../../inputs/SearchBar';

// Icons
import EditIcon from '../../../../../../assets/icons/edit-icon.svg';
import { textSearchFilter } from '../../../textSearchFilter';

const columnHelper = createColumnHelper<dataTable>();

export const TeamsManagementTable = ({
    data,
    onEditTeam,
    onDeleteTeam,
}: TableProps): JSX.Element => {
    const { t } = useTranslation(['tables', 'common', 'management']);
    const [globalFilter, setGlobalFilter] = useState('');

    // Table columns
    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: () => teamsColumnNames.name.label,
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('concessionNames', {
                header: () => teamsColumnNames.concessions.label,
                cell: (info) => {
                    const names = info.getValue();
                    if (!names || names.length === 0) return '-';
                    return (
                        <TagsContainer>
                            {names.map((name, idx) => (
                                <Tag key={idx}>{name}</Tag>
                            ))}
                        </TagsContainer>
                    );
                },
            }),
            columnHelper.accessor('roadNames', {
                header: () => teamsColumnNames.roads.label,
                cell: (info) => {
                    const names = info.getValue();
                    if (!names || names.length === 0) return '-';
                    return (
                        <TagsContainer>
                            {names.slice(0, 3).map((name, idx) => (
                                <Tag key={idx}>{name}</Tag>
                            ))}
                            {names.length > 3 && <Tag>+{names.length - 3}</Tag>}
                        </TagsContainer>
                    );
                },
            }),
            columnHelper.accessor('userNames', {
                header: () => teamsColumnNames.users.label,
                cell: (info) => {
                    const names = info.getValue();
                    if (!names || names.length === 0) return '-';
                    return (
                        <TagsContainer>
                            {names.slice(0, 2).map((name, idx) => (
                                <Tag key={idx}>{name}</Tag>
                            ))}
                            {names.length > 2 && <Tag>+{names.length - 2}</Tag>}
                        </TagsContainer>
                    );
                },
            }),
            columnHelper.accessor('vehiclePlates', {
                header: () => teamsColumnNames.vehicles.label,
                cell: (info) => {
                    const plates = info.getValue();
                    if (!plates || plates.length === 0) return '-';
                    return (
                        <TagsContainer>
                            {plates.slice(0, 3).map((plate, idx) => (
                                <Tag key={idx}>{plate}</Tag>
                            ))}
                            {plates.length > 3 && <Tag>+{plates.length - 3}</Tag>}
                        </TagsContainer>
                    );
                },
            }),
            columnHelper.display({
                id: 'actions',
                header: () => '',
                cell: ({ row }) => {
                    const team = row.original;
                    return (
                        <ActionCell>
                            <ActionButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onEditTeam) {
                                        onEditTeam(team.id, {
                                            name: team.name,
                                            concessionIDs: team.concessionIDs || [],
                                            roadIDs: team.roadIDs || [],
                                            userIDs: team.userIDs || [],
                                            vehicleIDs: team.vehicleIDs || [],
                                        });
                                    }
                                }}
                                title={t('management:teams.edit', 'Editar equipo')}
                            >
                                <img src={EditIcon} alt="Editar" style={{ width: '1.8rem', height: '1.8rem' }} />
                            </ActionButton>
                        </ActionCell>
                    );
                },
            }),
        ],
        [onEditTeam, onDeleteTeam, t]
    );

    const table = useReactTable({
        data: data || [],
        columns,
        state: {
            globalFilter,
        },
        onGlobalFilterChange: setGlobalFilter,
        // Sin acentos y por palabras (antes el 'auto' de TanStack, literal).
        globalFilterFn: textSearchFilter(),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const onFilter = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setGlobalFilter(e.target.value);
    }, []);

    return (
        <ContainerTable>
            <TeamsTableContainer>
                <Typography size="L" variant="semiBold">
                    {t('management:teamsList')}
                </Typography>
                <FiltersContainer>
                    <SearchBar
                        variant="form"
                        onChangeInput={onFilter}
                    />
                </FiltersContainer>
                {/* El TableContainer es el que recorta y da scroll horizontal.
                    Sin él la tabla se salía de la tarjeta (777 px dentro de un
                    hueco de 456 px). Es el mismo envoltorio que usan las otras
                    ocho tablas de Gestión. */}
                <TableContainer>
                    <Table>
                        <Thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Th key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </Th>
                                    ))}
                                </tr>
                            ))}
                        </Thead>
                        <Tbody>
                            {table.getRowModel().rows.map((row) => (
                                <TrStyled
                                    key={row.id}
                                    onClick={() => {
                                        const team = row.original;
                                        if (onEditTeam) {
                                            onEditTeam(team.id, {
                                                name: team.name,
                                                concessionIDs: team.concessionIDs || [],
                                                roadIDs: team.roadIDs || [],
                                                userIDs: team.userIDs || [],
                                                vehicleIDs: team.vehicleIDs || [],
                                            });
                                        }
                                    }}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <Td key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </Td>
                                    ))}
                                </TrStyled>
                            ))}
                            {table.getRowModel().rows.length === 0 && (
                                <tr>
                                    <Td colSpan={columns.length}>
                                        <Typography size="S" >
                                            {t('common:messages.noResults')}
                                        </Typography>
                                    </Td>
                                </tr>
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            </TeamsTableContainer>
        </ContainerTable>
    );
};
